import type { UploadTask } from '../types/uploads';

const DATABASE_NAME = 'vision-lab-transfers';
const DATABASE_VERSION = 1;
const STORE_NAME = 'file-handles';

interface StoredFileHandle {
  key: string;
  uid: string;
  uploadTaskId: string;
  name: string;
  size: number;
  lastModified: number;
  handle: FileSystemFileHandle;
  savedAt: number;
}

function recordKey(uid: string, uploadTaskId: string): string {
  return `${uid}:${uploadTaskId}`;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('file_handle_database_open_failed'));
  });
}

async function transaction<T>(
  mode: IDBTransactionMode,
  run: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const database = await openDatabase();
  try {
    return await new Promise<T>((resolve, reject) => {
      const request = run(database.transaction(STORE_NAME, mode).objectStore(STORE_NAME));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('file_handle_database_error'));
    });
  } finally {
    database.close();
  }
}

async function save(
  uid: string,
  uploadTaskId: string,
  file: File,
  handle: FileSystemFileHandle,
): Promise<void> {
  const record: StoredFileHandle = {
    key: recordKey(uid, uploadTaskId),
    uid,
    uploadTaskId,
    name: file.name,
    size: file.size,
    lastModified: file.lastModified,
    handle,
    savedAt: Date.now(),
  };
  try {
    await transaction('readwrite', (store) => store.put(record));
  } catch {
    // Safari/private browsing may expose IndexedDB while rejecting writes. The live SharedWorker
    // handoff remains available and reload recovery falls back to an explicit reattach.
  }
}

async function readFile(uid: string, item: UploadTask): Promise<File | undefined> {
  try {
    const record = await transaction<StoredFileHandle | undefined>('readonly', (store) =>
      store.get(recordKey(uid, item.id)),
    );
    if (!record || record.name !== item.displayName || record.size !== item.totalBytes)
      return undefined;
    const file = await record.handle.getFile();
    if (
      file.name !== record.name ||
      file.size !== record.size ||
      file.lastModified !== record.lastModified
    ) {
      return undefined;
    }
    return file;
  } catch {
    // Permission may not survive a browser restart. Permission prompts require a user gesture, so
    // boot recovery falls back to the normal Reattach action instead of prompting automatically.
    return undefined;
  }
}

async function remove(uid: string, uploadTaskId: string): Promise<void> {
  try {
    await transaction('readwrite', (store) => store.delete(recordKey(uid, uploadTaskId)));
  } catch {
    // Storage can be unavailable during page teardown; stale handles contain no file bytes.
  }
}

export const uploadFileHandleStore = { save, readFile, remove };
